import { Link, SpaceBetween } from '@amzn/awsui-components-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToolPanel } from '../../store/tool-panel.context';
import { i18nKeys } from '../../utils/i18n.utils';
import { HelpPanel } from './HelpPanel';

export interface WatchGuideLinkProps {
  /** URL of the guide video (mp4) to play in the help panel. */
  videoUrl: string;
  /** Translated title shown as the help panel heading (e.g. "Joining a Jam Event"). */
  title: string;
}

/**
 * The content rendered inside the side help panel. Kept as its own component so
 * it can read the tool-panel open state from context and pause the video when
 * the panel is collapsed.
 */
export const GuideVideoPanel: React.FC<{ videoUrl: string; title: string }> = ({ videoUrl, title }) => {
  const { t } = useTranslation();
  const { showToolPanel, toolPanelOpen } = useToolPanel();
  const videoRef = useRef<HTMLVideoElement>(null);

  const panelVisible = showToolPanel && toolPanelOpen;

  useEffect(() => {
    if (!panelVisible && videoRef.current) {
      videoRef.current.pause();
    }
  }, [panelVisible]);

  return (
    <HelpPanel title={title}>
      <SpaceBetween size="m">
        <video key={videoUrl} ref={videoRef} style={{ width: '100%' }} controls>
          <source src={videoUrl} type="video/mp4" />
          <track kind="captions" />
          {t(i18nKeys.events.eventDetails.labels.preEventLobbyVideo.noVideoSupport)}
        </video>
      </SpaceBetween>
    </HelpPanel>
  );
};

/**
 * Renders as a `Link variant="info"` placed next to a section/page title.
 */
const WatchGuideLink: React.FC<WatchGuideLinkProps> = ({ videoUrl, title }) => {
  const { t } = useTranslation();
  const { toggleShowToolPanel, toggleToolPanel, renderToolPanelContent } = useToolPanel();

  const showHelpPanel = () => {
    toggleShowToolPanel(true);
    toggleToolPanel(true);
    renderToolPanelContent(<GuideVideoPanel videoUrl={videoUrl} title={title} />);
  };

  return (
    <Link variant="info" onFollow={() => showHelpPanel()}>
      {t(i18nKeys.general.info)}
    </Link>
  );
};

export default WatchGuideLink;
